import { i18nDateToString } from 'modules/core/lib/dates'
import type { DateRange } from '@factorialco/f0-react/dist/experimental'
import type { DateFilter } from 'generated/resources/graphql'

type DateFilterOperation = Exclude<keyof DateFilter, 'between'>

type DateFilterMapping =
  | 'between'
  | {
      from?: DateFilterOperation
      to?: DateFilterOperation
    }

/**
 * Maps a Date or DateRange to GraphQL DateFilter
 *
 * @param date - A single Date or DateRange from the UI component
 * @param mapping - How to map from/to dates to filter operations
 *
 * @example
 * // Single Date with eq
 * { createdAt: mapDateRangeToDateFilter(new Date(), { from: 'eq' }) }
 * // Returns: { createdAt: { eq: '2024-01-01' } }
 *
 * @example
 * // DateRange with custom operations
 * { createdAt: mapDateRangeToDateFilter(dateRange, { from: 'gt', to: 'lteq' }) }
 * // Returns: { createdAt: { gt: '2024-01-01', lteq: '2024-12-31' } }
 *
 * @example
 * // DateRange with between
 * { createdAt: mapDateRangeToDateFilter(dateRange, 'between') }
 * // With both dates: { createdAt: { between: ['2024-01-01', '2024-12-31'] } }
 * // With only from: { createdAt: { gteq: '2024-01-01' } }
 *
 * @example
 * // Default range behavior (gteq/lteq)
 * { startDate: mapDateRangeToDateFilter(dateRange, { from: 'gteq', to: 'lteq' }) }
 * // Returns: { startDate: { gteq: '2024-01-01', lteq: '2024-12-31' } }
 */
export const mapDateRangeToDateFilter = (
  date: Date | DateRange | undefined | null,
  mapping: DateFilterMapping
): DateFilter | undefined => {
  if (!date) return undefined

  // Handle single Date
  if (date instanceof Date) {
    const dateStr = i18nDateToString(date, 'backend')

    if (mapping === 'between') {
      return { between: [dateStr, dateStr] }
    }

    // For single Date, only use 'from' operation
    if (typeof mapping === 'object' && mapping.from) {
      return { [mapping.from]: dateStr }
    }

    // Default to eq for single Date
    return { eq: dateStr }
  }

  // Handle DateRange
  if (!date.from) return undefined

  const fromDate = i18nDateToString(date.from, 'backend')
  const toDate = date.to ? i18nDateToString(date.to, 'backend') : undefined

  // Handle 'between' mapping
  if (mapping === 'between') {
    if (toDate) {
      return { between: [fromDate, toDate] }
    }
    // Fallback to gteq if only from date
    return { gteq: fromDate }
  }

  const filter: DateFilter = {}

  if (mapping.from) {
    filter[mapping.from] = fromDate
  }

  if (mapping.to && toDate) {
    filter[mapping.to] = toDate
  }

  return filter
}
